import { useState, useEffect } from 'react';
import db, { resetDatabase } from '@/db';
import { setTrainingMode } from '@/lib/training';
import { SyncAPI } from '@/lib/firebaseSync';
import { loadServerConfig, saveServerConfig } from '@/lib/serverConfig';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Settings, Users, Hash, Scale, Download, Upload, AlertTriangle, Receipt, Coffee, Phone, ExternalLink, ShieldAlert, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeBrPhone, formatBrPhone } from '@/lib/notifications';
import IntegrationsCard from '@/components/IntegrationsCard';
import DeviceAndPilotCard from '@/components/DeviceAndPilotCard';
import ScaleBarcodeCard from '@/components/ScaleBarcodeCard';
import RequireSessionCard from '@/components/RequireSessionCard';

export default function SettingsPage() {
  const [tableCount, setTableCount] = useState(10);
  const [businessName, setBusinessName] = useState('Kaven Local');
  const [buffetPrice, setBuffetPrice] = useState('59.90');
  const [coffeePrice, setCoffeePrice] = useState('15.00');
  const [restaurantPhone, setRestaurantPhone] = useState('');
  const [restaurantGreeting, setRestaurantGreeting] = useState('');
  const [trainingMode, setTrainingModeState] = useState(false);
  // Fase 30 — Reset com confirmação forte
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  // v7.44 — Forçar Sincronização (recuperação rápida) — limpa só o cache
  // local e recarrega. Bem menos destrutivo que o "ZERAR TUDO" da Zona
  // de Perigo. Útil quando o app está em estado inconsistente após cache
  // antigo, schema migration travada, ou listener Firebase em loop.
  const [forcingSync, setForcingSync] = useState(false);

  const loadSettings = async () => {
    // v7.18 — chaves NÃO-sensíveis vêm do servidor (Mongo), com fallback
    // automático para o cache Dexie em caso de offline. Chaves locais
    // (tableCount, trainingMode local toggle) continuam no Dexie.
    let serverCfg = {};
    try {
      const body = await loadServerConfig();
      serverCfg = body?.config || {};
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[SettingsPage] loadServerConfig falhou, usando Dexie:', err);
    }
    const tc = await db.config.get('tableCount');
    const tm = await db.config.get('trainingMode');
    if (tc) setTableCount(tc.value);
    setBusinessName(serverCfg.restaurantName ?? (await db.config.get('businessName'))?.value ?? 'Kaven Local');
    setBuffetPrice(String(serverCfg.buffetPricePerKg ?? (await db.config.get('buffetPricePerKg'))?.value ?? '59.90'));
    setCoffeePrice(String(serverCfg.coffeeComboPrice ?? (await db.config.get('coffeeComboPrice'))?.value ?? '15.00'));
    setRestaurantPhone(serverCfg.restaurantPhone ?? (await db.config.get('restaurantPhone'))?.value ?? '');
    setRestaurantGreeting(serverCfg.restaurantGreeting ?? (await db.config.get('restaurantGreeting'))?.value ?? '');
    setTrainingModeState(!!tm?.value);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSaveTableCount = async () => {
    const count = parseInt(tableCount, 10);
    if (count < 1 || count > 100) {
      toast.error('Numero de mesas deve ser entre 1 e 100');
      return;
    }

    await db.config.put({ key: 'tableCount', value: count });

    // Adjust tables
    const existingTables = await db.mesas.toArray();
    const currentMax = existingTables.length;

    if (count > currentMax) {
      const newTables = [];
      for (let i = currentMax + 1; i <= count; i++) {
        newTables.push({
          number: i,
          status: 'available',
          total: 0,
          openedAt: null,
          openedBy: null,
        });
      }
      await db.mesas.bulkAdd(newTables);
    } else if (count < currentMax) {
      // Remove only available extra tables
      const toRemove = existingTables
        .filter((t) => t.number > count && t.status === 'available')
        .map((t) => t.id);
      await db.mesas.bulkDelete(toRemove);
    }

    toast.success(`Mesas ajustadas para ${count}`);
  };

  const handleSaveBusinessName = async () => {
    try {
      await saveServerConfig({ restaurantName: businessName });
      await db.config.put({ key: 'businessName', value: businessName });  // cache offline
      toast.success('Nome atualizado no servidor');
    } catch (err) {
      toast.error('Falha: ' + (err?.message || err));
    }
  };

  // v7.89 — handleAddOperator/handleDeleteOperator removidos: a gestão de
  // operadores migrou para a aba Equipe (servidor + bcrypt + Admin Master).

  const handleSaveBuffetPrice = async () => {
    const price = parseFloat(buffetPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Preco invalido');
      return;
    }
    try {
      await saveServerConfig({ buffetPricePerKg: price });
      await db.config.put({ key: 'buffetPricePerKg', value: price });
      toast.success('Preco do buffet atualizado no servidor');
    } catch (err) {
      toast.error('Falha: ' + (err?.message || err));
    }
  };

  const handleSaveCoffeePrice = async () => {
    const price = parseFloat(coffeePrice);
    if (Number.isNaN(price) || price <= 0) {
      toast.error('Preco invalido');
      return;
    }
    try {
      await saveServerConfig({ coffeeComboPrice: price });
      await db.config.put({ key: 'coffeeComboPrice', value: price });
      toast.success('Valor do combo café salvo no servidor');
    } catch (err) {
      toast.error('Falha: ' + (err?.message || err));
    }
  };

  const handleSaveRestaurantPhone = async () => {
    const normalized = normalizeBrPhone(restaurantPhone);
    if (restaurantPhone.trim() && !normalized) {
      toast.error('Telefone inválido (mínimo 10 dígitos)');
      return;
    }
    try {
      await saveServerConfig({ restaurantPhone: restaurantPhone.trim() });
      await db.config.put({ key: 'restaurantPhone', value: restaurantPhone.trim() });
      toast.success(normalized ? `WhatsApp salvo: ${formatBrPhone(normalized)}` : 'Telefone removido');
    } catch (err) {
      toast.error('Falha: ' + (err?.message || err));
    }
  };

  const handleSaveRestaurantGreeting = async () => {
    const g = restaurantGreeting.trim();
    if (!g) {
      toast.error('Mensagem não pode ficar vazia');
      return;
    }
    try {
      await saveServerConfig({ restaurantGreeting: g });
      await db.config.put({ key: 'restaurantGreeting', value: g });
      toast.success('Saudação salva no servidor');
    } catch (err) {
      toast.error('Falha: ' + (err?.message || err));
    }
  };

  const testRestaurantWhatsApp = () => {
    const normalized = normalizeBrPhone(restaurantPhone);
    if (!normalized) {
      toast.error('Configure e salve o telefone antes de testar');
      return;
    }
    const text = restaurantGreeting.trim() || 'Teste de configuração Kaven';
    window.open(`https://wa.me/${normalized}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };


  const handleExportBackup = async () => {
    const data = {
      exportedAt: new Date().toISOString(),
      version: '1.0',
      config: await db.config.toArray(),
      // v7.89 — operadores NÃO entram no backup: o servidor é a fonte da
      // verdade e importar um backup antigo ressuscitava usuários/PINs.
      mesas: await db.mesas.toArray(),
      beverages: await db.beverages.toArray(),
      orders: await db.orders.toArray(),
      salesHistory: await db.salesHistory.toArray(),
    };
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kaven-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Backup exportado!');
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.version || !data.config) {
        toast.error('Arquivo de backup invalido');
        return;
      }
      if (!window.confirm('Isso substituira TODOS os dados atuais. Continuar?')) return;
      await db.config.clear();
      await db.mesas.clear();
      await db.beverages.clear();
      await db.orders.clear();
      await db.salesHistory.clear();
      if (data.config) await db.config.bulkPut(data.config);
      // v7.89 — data.operators de backups antigos é IGNORADO de propósito:
      // restaurar operadores locais ressuscitava fantasmas e PINs antigos.
      if (data.mesas) await db.mesas.bulkAdd(data.mesas.map(({ id, ...rest }) => rest));
      if (data.beverages) await db.beverages.bulkAdd(data.beverages.map(({ id, ...rest }) => rest));
      if (data.orders) await db.orders.bulkAdd(data.orders.map(({ id, ...rest }) => rest));
      if (data.salesHistory) await db.salesHistory.bulkAdd(data.salesHistory.map(({ id, ...rest }) => rest));
      toast.success('Backup importado! Recarregando...');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error('Erro ao importar backup');
    }
    e.target.value = '';
  };

  /**
   * v7.44 — Forçar Sincronização (recuperação rápida).
   *
   * Caso de uso: o operador percebe estado inconsistente (cardápio sumido,
   * pedidos não aparecem, app preso em loop). Em vez de abrir suporte ou
   * usar o ZERAR TUDO (que apaga config), este botão:
   *   1. Limpa SOMENTE o cache local (IndexedDB)
   *   2. Recarrega a página
   *
   * Sem confirmação destrutiva — o Firebase repopula tudo no próximo boot
   * (Pratos, comandas, config compartilhada, etc). Não há perda real de
   * dados, apenas evicção de cache.
   */
  const handleForceSync = async () => {
    if (forcingSync) return;
    setForcingSync(true);
    try {
      await resetDatabase();
      toast.success('Cache limpo. Recarregando para ressincronizar...');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[FORCE_SYNC] resetDatabase falhou:', err);
      toast.error('Erro ao limpar cache — recarregando mesmo assim');
    }
    // Reload sempre, mesmo em caso de falha — recarga limpa qualquer
    // estado JS pendente.
    setTimeout(() => window.location.reload(), 600);
  };

  const handleResetDatabase = async () => {
    if (resetConfirmText.trim().toUpperCase() !== 'ZERAR TUDO') {
      toast.error('Digite ZERAR TUDO para prosseguir');
      return;
    }
    setResetting(true);
    try {
      // 1) Limpa Firebase primeiro (enquanto auth ainda está ativo).
      //    Se falhar, segue mesmo assim — não quer travar reset por causa de rede.
      try {
        const wipe = await SyncAPI.wipeRemote();
        if (wipe?.ok) {
          // eslint-disable-next-line no-console
          console.log('[RESET] Firebase wipe OK:', wipe.deleted);
        } else {
          // eslint-disable-next-line no-console
          console.warn('[RESET] Firebase wipe pulado:', wipe?.reason);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RESET] Firebase wipe erro (ignorado):', err?.message || err);
      }
      // 2) Reset local
      await resetDatabase();
      toast.success('Sistema zerado (local + Firebase). Recarregando...');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err?.message || 'Falha ao resetar');
      setResetting(false);
    }
  };

  const handleToggleTraining = async (enabled) => {
    await setTrainingMode(enabled);
    setTrainingModeState(enabled);
    window.dispatchEvent(new CustomEvent('kaven:training-toggle'));
    toast.success(enabled ? '⚠️ Modo Treinamento ATIVADO' : 'Modo Treinamento desativado');
  };

  const handleExportCommands = async () => {
    const all = await db.commands.toArray();
    if (all.length === 0) return toast.error('Nenhuma comanda registrada');

    // JSON
    const json = JSON.stringify({ exportedAt: new Date().toISOString(), total: all.length, commands: all }, null, 2);
    const jsonBlob = new Blob([json], { type: 'application/json' });
    const jsonUrl = URL.createObjectURL(jsonBlob);
    const jsonLink = document.createElement('a');
    jsonLink.href = jsonUrl;
    jsonLink.download = `kaven-comandas-${new Date().toISOString().slice(0, 10)}.json`;
    jsonLink.click();
    URL.revokeObjectURL(jsonUrl);

    // CSV (compatível com Excel/Sheets)
    const headers = ['numero', 'status', 'buffetTotal', 'itemsTotal', 'total', 'paymentMethod', 'isTraining', 'createdAt', 'paidAt'];
    const rows = all.map((c) => [
      c.number,
      c.status,
      (c.buffetTotal || 0).toFixed(2),
      (c.itemsTotal || 0).toFixed(2),
      (c.total || 0).toFixed(2),
      c.paymentMethod || '',
      c.isTraining ? 'SIM' : 'NAO',
      c.createdAt || '',
      c.paidAt || '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const csvBlob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const csvLink = document.createElement('a');
    csvLink.href = csvUrl;
    csvLink.download = `kaven-comandas-${new Date().toISOString().slice(0, 10)}.csv`;
    csvLink.click();
    URL.revokeObjectURL(csvUrl);

    toast.success(`${all.length} comandas exportadas (JSON + CSV)`);
  };

  return (
    <div data-testid="settings-page" className="p-6 lg:p-8">
      <div className="mb-8">
        <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">
          Configuracoes
        </h2>
        <p className="text-sm text-zinc-500 mt-1">Ajustes gerais do sistema</p>
      </div>

      <div className="max-w-2xl space-y-6">
        <DeviceAndPilotCard />
        <IntegrationsCard />
        {/* Training Mode (Fase 8) */}
        <div data-testid="training-mode-card" className={`border-2 rounded-xl p-6 transition-all ${trainingMode ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`}>
          <div className="flex items-start gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${trainingMode ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
              <AlertTriangle size={18} strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <p className="font-heading font-bold text-slate-900">Modo Treinamento</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Quando ativo, novas comandas são marcadas como teste e <strong>não contam no financeiro real</strong>. Ideal para treinar novos funcionários.
              </p>
            </div>
            <Switch
              data-testid="training-mode-switch"
              checked={trainingMode}
              onCheckedChange={handleToggleTraining}
            />
          </div>
          {trainingMode && (
            <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-800 bg-amber-100/60 rounded-lg p-3">
              ⚠️ Banner amarelo aparece no topo. Lançamentos no <strong>/garcom</strong> e <strong>/balanca</strong> são gravados com <code className="bg-amber-200 px-1 rounded">isTraining=true</code> e filtrados dos relatórios.
            </div>
          )}
        </div>

        {/* Business Name */}
        <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings size={18} className="text-zinc-400" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400">
              Nome do Estabelecimento
            </p>
          </div>
          <div className="flex gap-3">
            <Input
              data-testid="business-name-input"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="h-12 border-2 border-zinc-200 rounded-xl text-base flex-1"
            />
            <button
              data-testid="save-business-name-btn"
              onClick={handleSaveBusinessName}
              className="bg-[#2563eb] text-white rounded-lg font-bold px-6 border-2 border-transparent hover:bg-[#1d4ed8] active:translate-y-[2px] transition-all min-h-[48px]"
            >
              Salvar
            </button>
          </div>
        </div>

        {/* Table Count */}
        <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Hash size={18} className="text-zinc-400" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400">
              Numero de Mesas
            </p>
          </div>
          <div className="flex gap-3">
            <Input
              data-testid="table-count-input"
              type="number"
              min="1"
              max="100"
              value={tableCount}
              onChange={(e) => setTableCount(e.target.value)}
              className="h-12 border-2 border-zinc-200 rounded-xl text-base font-mono w-32"
            />
            <button
              data-testid="save-table-count-btn"
              onClick={handleSaveTableCount}
              className="bg-[#2563eb] text-white rounded-lg font-bold px-6 border-2 border-transparent hover:bg-[#1d4ed8] active:translate-y-[2px] transition-all min-h-[48px]"
            >
              Aplicar
            </button>
          </div>
          <p className="text-xs text-zinc-400 mt-2">
            Mesas ocupadas nao serao removidas ao diminuir o total
          </p>
        </div>

        {/* Operators — v7.89: gestão migrou pra aba Equipe (servidor + Admin Master) */}
        <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Users size={18} className="text-zinc-400" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400">
              Operadores
            </p>
          </div>
          <p className="text-sm text-zinc-600">
            A gestão de operadores agora é feita na aba <a href="/equipe" className="font-bold text-[#2563eb] hover:underline">Equipe</a>,
            com PIN criptografado no servidor e acesso restrito ao Admin Master.
          </p>
        </div>

        {/* Buffet Price per Kg */}
        <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Scale size={18} className="text-zinc-400" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400">
              Preco do Buffet por Kg
            </p>
          </div>
          <div className="flex gap-3">
            <Input
              data-testid="buffet-price-input"
              type="number"
              step="0.01"
              min="0"
              value={buffetPrice}
              onChange={(e) => setBuffetPrice(e.target.value)}
              className="h-12 border-2 border-zinc-200 rounded-xl text-base font-mono w-40"
            />
            <button
              data-testid="save-buffet-price-btn"
              onClick={handleSaveBuffetPrice}
              className="bg-[#2563eb] text-white rounded-lg font-bold px-6 border-2 border-transparent hover:bg-[#1d4ed8] active:translate-y-[2px] transition-all min-h-[48px]"
            >
              Salvar
            </button>
          </div>
          <p className="text-xs text-zinc-400 mt-2">
            Usado como preco padrao na pagina da Balanca
          </p>
        </div>

        {/* Fase 29 — Configuração de etiqueta da balança (EAN-13) */}
        <ScaleBarcodeCard />

        {/* Fase 31.2 — Exigir caixa aberto para criar comandas */}
        <RequireSessionCard />

        {/* Coffee Combo Price (Fase 8.5) */}
        <div data-testid="coffee-price-card" className="bg-amber-50/40 border-2 border-amber-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center">
              <Coffee size={14} strokeWidth={2} />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-700">
              Valor Fixo do Café da Manhã
            </p>
          </div>
          <div className="flex gap-3">
            <Input
              data-testid="coffee-price-input"
              type="number"
              step="0.01"
              min="0"
              value={coffeePrice}
              onChange={(e) => setCoffeePrice(e.target.value)}
              className="h-12 border-2 border-amber-200 bg-white rounded-xl text-base font-mono w-40"
            />
            <button
              data-testid="save-coffee-price-btn"
              onClick={handleSaveCoffeePrice}
              className="bg-amber-600 text-white rounded-lg font-bold px-6 border-2 border-transparent hover:bg-amber-700 active:translate-y-[2px] transition-all min-h-[48px]"
            >
              Salvar
            </button>
          </div>
          <p className="text-xs text-amber-700/80 mt-2">
            Valor do combo lançado pelo botão <strong>☕ Abrir Comanda Café</strong> em /garcom e /caixa
          </p>
        </div>

        {/* WhatsApp do Restaurante (Fase 11 - Gestão de Contato) */}
        <div data-testid="restaurant-phone-card" className="bg-gradient-to-br from-emerald-50/60 to-white border-2 border-emerald-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center">
              <Phone size={13} strokeWidth={2.25} />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-800">
              WhatsApp do Restaurante
            </p>
          </div>

          <label className="text-[11px] uppercase tracking-wider font-bold text-emerald-900/70 mb-1 block">
            Número (com DDD)
          </label>
          <div className="flex gap-2 mb-1">
            <Input
              data-testid="restaurant-phone-input"
              type="tel"
              inputMode="tel"
              value={restaurantPhone}
              onChange={(e) => setRestaurantPhone(e.target.value)}
              placeholder="(21) 99999-0000"
              className="h-12 border-2 border-emerald-200 bg-white rounded-xl text-base font-mono flex-1"
            />
            <button
              data-testid="save-restaurant-phone-btn"
              onClick={handleSaveRestaurantPhone}
              className="bg-emerald-600 text-white rounded-lg font-bold px-5 border-2 border-transparent hover:bg-emerald-700 active:translate-y-[2px] transition-all min-h-[48px]"
            >
              Salvar
            </button>
            <button
              data-testid="test-restaurant-phone-btn"
              onClick={testRestaurantWhatsApp}
              className="bg-white border-2 border-emerald-300 text-emerald-700 rounded-lg font-bold px-4 hover:bg-emerald-50 active:translate-y-[2px] transition-all min-h-[48px] flex items-center gap-1.5"
              title="Abrir conversa de teste no WhatsApp"
            >
              <ExternalLink size={14} /> Testar
            </button>
          </div>
          {restaurantPhone && normalizeBrPhone(restaurantPhone) && (
            <p data-testid="restaurant-phone-preview" className="text-[11px] text-emerald-700 mb-3">
              ✓ Salvo como <span className="font-mono font-bold">{formatBrPhone(normalizeBrPhone(restaurantPhone))}</span>
            </p>
          )}

          <label className="text-[11px] uppercase tracking-wider font-bold text-emerald-900/70 mb-1 block mt-3">
            Mensagem pré-preenchida (fallback humano)
          </label>
          <div className="flex gap-2">
            <Input
              data-testid="restaurant-greeting-input"
              value={restaurantGreeting}
              onChange={(e) => setRestaurantGreeting(e.target.value)}
              placeholder="Olá! Preciso de ajuda com meu pedido"
              maxLength={140}
              className="h-12 border-2 border-emerald-200 bg-white rounded-xl text-sm flex-1"
            />
            <button
              data-testid="save-restaurant-greeting-btn"
              onClick={handleSaveRestaurantGreeting}
              className="bg-emerald-600 text-white rounded-lg font-bold px-5 border-2 border-transparent hover:bg-emerald-700 active:translate-y-[2px] transition-all min-h-[48px]"
            >
              Salvar
            </button>
          </div>

          <p className="text-xs text-emerald-800/75 mt-3 leading-relaxed">
            Usado em <strong>3 lugares</strong>:
            <br />• 🤖 <strong>Robô WhatsApp</strong> notifica este número quando chega pedido novo em <code className="font-mono text-[11px] bg-emerald-100 px-1 rounded">/delivery</code>
            <br />• 📞 Botão <strong>"Falar com Atendente"</strong> no <code className="font-mono text-[11px] bg-emerald-100 px-1 rounded">/delivery</code> (fallback humano se o cliente desistir do Menu Digital)
            <br />• 📱 Link direto de suporte em <code className="font-mono text-[11px] bg-emerald-100 px-1 rounded">/rastreio/:id</code>
          </p>
        </div>

        {/* Backup & Export */}
        <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Download size={18} className="text-zinc-400" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400">
              Backup e Exportacao
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              data-testid="export-backup-btn"
              onClick={handleExportBackup}
              className="flex items-center justify-center gap-2 bg-white text-zinc-900 rounded-lg font-bold border-2 border-zinc-300 hover:border-zinc-900 active:translate-y-[2px] transition-all py-4 min-h-[48px]"
            >
              <Download size={18} />
              Exportar JSON
            </button>
            <label
              data-testid="import-backup-btn"
              className="flex items-center justify-center gap-2 bg-white text-zinc-900 rounded-lg font-bold border-2 border-zinc-300 hover:border-zinc-900 active:translate-y-[2px] transition-all py-4 min-h-[48px] cursor-pointer"
            >
              <Upload size={18} />
              Importar JSON
              <input
                type="file"
                accept=".json"
                onChange={handleImportBackup}
                className="hidden"
              />
            </label>
          </div>
          <button
            data-testid="export-commands-btn"
            onClick={handleExportCommands}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 active:translate-y-[1px] transition-all py-4 min-h-[48px]"
          >
            <Receipt size={18} strokeWidth={1.75} />
            Exportar Comandas (JSON + CSV/Excel)
          </button>
          <p className="text-xs text-zinc-400 mt-2">
            Salve ou restaure todos os dados do sistema em JSON. Comandas também são exportadas em CSV (compatível com Excel).
          </p>
        </div>

        {/* v7.44 — Recuperação rápida (não destrutiva). Antes da Zona de
            Perigo pra evitar que o operador clique no ZERAR TUDO quando
            só queria ressincronizar. */}
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-700 mb-4">
            Recuperação Rápida
          </p>
          <button
            data-testid="force-sync-btn"
            onClick={handleForceSync}
            disabled={forcingSync}
            className="w-full flex items-center justify-center gap-2 bg-amber-600 text-white rounded-lg font-bold border-2 border-amber-700 hover:bg-amber-700 active:translate-y-[2px] transition-all py-4 min-h-[48px] disabled:opacity-60 disabled:cursor-wait"
          >
            <RefreshCw size={18} strokeWidth={2} className={forcingSync ? 'animate-spin' : ''} />
            {forcingSync ? 'Limpando cache…' : 'Forçar Sincronização'}
          </button>
          <p className="text-xs text-amber-900/70 mt-2">
            Limpa o cache local e recarrega o app. O Firebase repopula automaticamente. Use quando o cardápio sumir, pedidos não aparecerem ou o app travar. <strong>Não apaga dados</strong> — só evicta cache.
          </p>
        </div>

        {/* Danger Zone */}
        <div className="bg-white border-2 border-red-200 rounded-xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-red-400 mb-4">
            Zona de Perigo
          </p>
          <button
            data-testid="reset-database-btn"
            onClick={() => setShowResetDialog(true)}
            className="w-full flex items-center justify-center gap-2 bg-white text-red-600 rounded-lg font-bold border-2 border-red-300 hover:bg-red-50 hover:border-red-500 active:translate-y-[2px] transition-all py-4 min-h-[48px]"
          >
            <ShieldAlert size={18} strokeWidth={2} />
            Zerar TODO o Sistema
          </button>
          <p className="text-xs text-zinc-400 mt-2">
            Remove <strong>tudo</strong>: mesas, comandas, pedidos, estoque, operadores, configurações, cardápio, bairros, fidelidade e logs. Ação irreversível. Só o admin deve fazer isso.
          </p>
        </div>
      </div>

      {/* v7.89 — Dialog "Novo Operador" removido: gestão migrou pra aba Equipe */}

      {/* Fase 30 — Dialog: Zerar todo o sistema (reset forte) */}
      <Dialog open={showResetDialog} onOpenChange={(o) => { if (!o && !resetting) { setShowResetDialog(false); setResetConfirmText(''); } }}>
        <DialogContent data-testid="settings-reset-dialog" className="max-w-md bg-white border-2 border-red-300 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-red-700 flex items-center gap-2">
              <ShieldAlert size={22} /> Zerar TODO o sistema?
            </DialogTitle>
            <DialogDescription>
              Vai apagar <strong>permanentemente</strong> todos os dados locais: mesas, comandas, estoque, cardápio, fidelidade, logs, configurações. Os operadores NÃO são afetados — eles vivem no servidor e serão ressincronizados após o reset.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-300 rounded-xl p-4">
            <p className="text-xs font-bold text-red-800 mb-3">
              ⚠️ Irreversível. Salve um backup JSON antes se quiser restaurar depois.
            </p>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">
              Digite <strong className="text-red-700">ZERAR TUDO</strong> para prosseguir
            </label>
            <Input
              data-testid="settings-reset-input"
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="ZERAR TUDO"
              className="h-11 border-2 border-red-300 rounded-xl font-mono font-bold text-center uppercase"
              autoFocus
              disabled={resetting}
            />
          </div>
          <div className="flex gap-2 mt-2">
            <button
              data-testid="settings-reset-cancel"
              onClick={() => { setShowResetDialog(false); setResetConfirmText(''); }}
              disabled={resetting}
              className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              data-testid="settings-reset-confirm"
              onClick={handleResetDatabase}
              disabled={resetting || resetConfirmText.trim().toUpperCase() !== 'ZERAR TUDO'}
              className="flex-1 h-11 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {resetting ? 'Zerando...' : 'Zerar tudo agora'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
